import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from 'react';

interface PaymentContextType {
  submittingPayment: boolean;
  setSubmittingPayment: Dispatch<SetStateAction<boolean>>;
  showInstallmentDecision: boolean;
  setShowInstallmentDecision: Dispatch<SetStateAction<boolean>>;
  pollingCapitalShop: boolean;
  setPollingCapitalShop: Dispatch<SetStateAction<boolean>>;
  sameBillingAsShipping: boolean;
  setSameBillingAsShipping: Dispatch<SetStateAction<boolean>>;
  disclosureDocumentToken: string | undefined;
  setDisclosureDocumentToken: Dispatch<SetStateAction<string | undefined>>;
  applicationFlowStatus: string | undefined;
  setApplicationFlowStatus: Dispatch<SetStateAction<string | undefined>>;
  showDisclosureModal: boolean;
  setShowDisclosureModal: Dispatch<SetStateAction<boolean>>;
  // opt_out subscription consent state, shared between the
  // <SubscriptionDisclosure> checkbox and the submit gate in useSubmitOrder.
  subscriptionConsentGiven: boolean;
  setSubscriptionConsentGiven: Dispatch<SetStateAction<boolean>>;
  subscriptionConsentError: boolean;
  setSubscriptionConsentError: Dispatch<SetStateAction<boolean>>;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

/*
  Custom hook to use payment context/utils
 */
const usePaymentContext = () => {
  const context = useContext(PaymentContext);
  if (context === undefined) {
    throw new Error(
      'usePaymentContext must be used within a PaymentContextProvider',
    );
  }
  return context;
};

interface PaymentContextProviderProps {
  children: ReactNode;
}

const PaymentContextProvider = ({ children }: PaymentContextProviderProps) => {
  // TODO: Manually handling submitting payment state since it can encompass order submission
  // and installment submission. Investigate using react-query to fully handle.
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [showInstallmentDecision, setShowInstallmentDecision] = useState(false);
  const [pollingCapitalShop, setPollingCapitalShop] = useState(false);
  const [sameBillingAsShipping, setSameBillingAsShipping] = useState(true);
  const [disclosureDocumentToken, setDisclosureDocumentToken] =
    useState(undefined);
  const [applicationFlowStatus, setApplicationFlowStatus] = useState(undefined);
  const [showDisclosureModal, setShowDisclosureModal] = useState(false);
  const [subscriptionConsentGiven, setSubscriptionConsentGiven] =
    useState(false);
  const [subscriptionConsentError, setSubscriptionConsentError] =
    useState(false);

  return (
    <PaymentContext.Provider
      value={{
        submittingPayment,
        setSubmittingPayment,
        showInstallmentDecision,
        setShowInstallmentDecision,
        pollingCapitalShop,
        setPollingCapitalShop,
        sameBillingAsShipping,
        setSameBillingAsShipping,
        disclosureDocumentToken,
        setDisclosureDocumentToken,
        applicationFlowStatus,
        setApplicationFlowStatus,
        showDisclosureModal,
        setShowDisclosureModal,
        subscriptionConsentGiven,
        setSubscriptionConsentGiven,
        subscriptionConsentError,
        setSubscriptionConsentError,
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
};

export { PaymentContextProvider, usePaymentContext };
